import type { WorldSlug } from '@vrlearning/lesson-schema';
import { MANIFEST, worldFiles, worldUrl, type Manifest } from './manifest.js';

/**
 * The headset's copy of the worlds (ADR-032). The service worker keeps every
 * world file it fetched in the cache `worlds` (vite.config.ts), CacheFirst:
 * a file's name is its content's hash, so it never changes under that name.
 * Nothing is precached; a world is fetched the first time a lesson uses it,
 * or before, while the headset waits in the lobby.
 */

/** The Workbox cache's name (vite.config.ts). */
export const WORLDS_CACHE = 'worlds';

/**
 * Deletes cached world files the manifest no longer names: a world changed
 * or left, its old files would otherwise stay for 180 days. Run at start.
 * Returns how many went. Never throws.
 */
export async function sweepWorldCache(
  manifest: Manifest = MANIFEST,
  storage: Pick<CacheStorage, 'open'> | undefined = globalThis.caches,
): Promise<number> {
  if (!storage) return 0;
  try {
    const cache = await storage.open(WORLDS_CACHE);
    const named = new Set(
      manifest.files.filter((file) => file.served).map((file) => worldUrl(file.stored)),
    );
    let swept = 0;
    for (const request of await cache.keys()) {
      if (named.has(new URL(request.url).pathname)) continue;
      if (await cache.delete(request)) swept++;
    }
    return swept;
  } catch {
    return 0;
  }
}

/** Whether this is a moment to fetch megabytes: not on a metered or save-data connection. */
export function mayPrefetch(
  connection: { type?: string; saveData?: boolean } | undefined = (
    globalThis.navigator as Navigator & { connection?: { type?: string; saveData?: boolean } }
  )?.connection,
): boolean {
  if (!connection) return true;
  if (connection.saveData) return false;
  return connection.type === undefined || !['cellular', 'bluetooth'].includes(connection.type);
}

/**
 * Fetches the files of these worlds one at a time, so thirty headsets in a
 * lobby do not pull 18 MB each at a lesson's start; the service worker keeps
 * them. Stops early when `keepGoing` says so (a lesson started). True when
 * every file is in.
 */
export async function prefetchWorlds(
  worlds: readonly WorldSlug[],
  options: {
    fetchImpl?: (url: string) => Promise<Response>;
    keepGoing?: () => boolean;
    manifest?: Manifest;
  } = {},
): Promise<boolean> {
  const fetchImpl = options.fetchImpl ?? ((url: string) => fetch(url));
  const keepGoing = options.keepGoing ?? ((): boolean => true);
  const stored = new Set<string>();
  for (const world of worlds) {
    for (const file of worldFiles(world, options.manifest ?? MANIFEST)) stored.add(file.stored);
  }
  let complete = true;
  for (const name of stored) {
    if (!keepGoing()) return false;
    try {
      const response = await fetchImpl(worldUrl(name));
      // Read to the end, so the service worker has all of it.
      await response.arrayBuffer();
      if (!response.ok) complete = false;
    } catch {
      complete = false;
    }
  }
  return complete;
}
